import React, { useRef } from 'react';
import { DatePicker, Form, TimePicker } from 'antd';
import { CalendarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { MomentToDate, useFormPath } from './index';
import 'moment/locale/fr';
import { useLingui } from '@lingui/react';
import locale_fr from 'antd/lib/date-picker/locale/fr_FR';
import locale_en from 'antd/lib/date-picker/locale/en_US';
import locale_he from 'antd/lib/date-picker/locale/he_IL';
import locale_it from 'antd/lib/date-picker/locale/it_IT';
import locale_pt from 'antd/lib/date-picker/locale/pt_PT';
import locale_ru from 'antd/lib/date-picker/locale/ru_RU';
import locale_de from 'antd/lib/date-picker/locale/de_DE';
import locale_es from 'antd/lib/date-picker/locale/es_ES';
import locale_gr from 'antd/lib/date-picker/locale/el_GR';
import locale_ro from 'antd/lib/date-picker/locale/ro_RO';
import DdnField from '../components/DdnField';
import { t } from "@lingui/core/macro";

// Le calendrier Antd (noms des mois/jours, boutons « Aujourd'hui »…) suit la
// locale passée en prop `locale`. Sans elle, il restait figé en français.
// On mappe la langue Lingui active vers la locale Antd correspondante (fallback FR).
const DATEPICKER_LOCALES = {
    fr: locale_fr,
    en: locale_en,
    he: locale_he,
    it: locale_it,
    pt: locale_pt,
    ru: locale_ru,
    de: locale_de,
    es: locale_es,
    gr: locale_gr,
    ro: locale_ro,
};

export default (formPlugin, namespace) => {
    return {
        Renderer: (props) => {

            const { listFieldProps, buildName, storePath } = useFormPath()
            const defaultNowApplied = useRef(false)
            const { i18n } = useLingui()
            const dpLocale = DATEPICKER_LOCALES[i18n?.locale] || locale_fr

            if (props.remove) {
                formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id)
            }

            if ((props.isPreviewMode || props.readOnly) && !props.data.fieldKey) {
                return null
            }

            const { data } = props

            let tooltip = null
            if (data.tooltip) {
                tooltip = {
                    title: data.tooltip,
                    icon: <InfoCircleOutlined />,
                }
            }

            let prefix = "[DATE]_"
            const range = data.range || false
            let withTime = false
            let picker = "date"
            let format = "DD/MM/YYYY"
            let disabledDate = (d) => false
            let showNow = true
            let component = "DATE"

            switch (data.dateFilter) {
                case formPlugin.DATE_FILTER_SELECT[0]: {
                    showNow = false
                    disabledDate = (d) => d > new Date()
                    break;
                }
                case formPlugin.DATE_FILTER_SELECT[1]: {
                    showNow = false
                    disabledDate = (d) => d < new Date()
                    break;
                }
                case formPlugin.DATE_FILTER_SELECT[2]: {
                    showNow = true
                    const tomorrow = new Date()
                    tomorrow.setDate(tomorrow.getDate() + 1)
                    disabledDate = (d) => d > tomorrow
                    break;
                }
                case formPlugin.DATE_FILTER_SELECT[3]: {
                    showNow = true
                    const tomorrow = new Date()
                    tomorrow.setDate(tomorrow.getDate() + 1)
                    disabledDate = (d) => d < tomorrow
                    break;
                }
            }

            switch (data.picker) {
                case formPlugin.DATE_PICKER[0]:
                    prefix = "[DATETIME]_"
                    withTime = true
                    format = "DD/MM/YYYY HH:mm"
                    break;
                case formPlugin.DATE_PICKER[1]:
                    prefix = "[YEAR]_"
                    picker = "year"
                    format = "YYYY"
                    break;
                case formPlugin.DATE_PICKER[2]:
                    prefix = "[MONTH]_"
                    picker = "month"
                    format = "MM/YYYY"
                    break;
                case formPlugin.DATE_PICKER[3]:
                    prefix = "[TIME]_"
                    picker = "time"
                    component = "TIME"
                    format = "HH:mm"
                    break;
            }

            let InputComponent = DatePicker
            if (component === "DATE" && range) InputComponent = DatePicker.RangePicker
            if (component === "TIME" && !range) InputComponent = TimePicker
            if (component === "TIME" && range) InputComponent = TimePicker.RangePicker

            const finalName = prefix + data.fieldKey
            const fieldName = buildName(finalName)
            const storeFieldName = [...storePath, finalName]

            const itemProps = {
                ...(listFieldProps || {}),
                label: `${data.label || t`Question`} :`,
                name: fieldName,
                rules: [{ required: data.required || false, message: t`Ce champ est obligatoire` }],
                tooltip: tooltip,
            }

            // defaultNow : applique la date du jour UNE seule fois (ref garde l'état à travers les renders)
            const renderWithDefault = (innerComponent) => {
                if (!data.defaultNow || defaultNowApplied.current) {
                    return <Form.Item {...itemProps}>{innerComponent}</Form.Item>
                }
                return (
                    <Form.Item noStyle shouldUpdate>
                        {(_form) => {
                            const current = _form.getFieldValue(storeFieldName)
                            if (current === undefined && !defaultNowApplied.current) {
                                defaultNowApplied.current = true
                                _form.setFieldValue(storeFieldName, new Date())
                            }
                            return <Form.Item {...itemProps}>{innerComponent}</Form.Item>
                        }}
                    </Form.Item>
                )
            }

            if (prefix === "[DATE]_" && !range) {
                return renderWithDefault(<DdnField />)
            }

            return renderWithDefault(
                <MomentToDate>
                    <InputComponent
                        size={"middle"}
                        locale={dpLocale}
                        style={{ width: "100%" }}
                        disabledDate={disabledDate}
                        picker={picker}
                        format={format}
                        showTime={withTime}
                        showNow={showNow}
                        inputReadOnly={component !== "TIME"}
                        needConfirm={false}
                    />
                </MomentToDate>
            )
        },
        namespace: namespace,
        id: 'form-date' + namespace,
        title: t`Champ date`,
        description: t`Élément de formulaire utilisable.`,
        icon: <CalendarOutlined />,
        iconColor: "#F39C12",
        version: 1,
        controls: {
            type: 'autoform',
            schema: {
                properties: {
                    range: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Plage de dates / heures`,
                        }
                    },
                    required: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Obligatoire`,
                        }
                    },
                    fieldKey: {
                        type: 'string',
                        uniforms: {
                            required: true,
                            label: t`Identifiant`,
                            placeholder: t`Identifiant du champ`,
                        }
                    },
                    label: {
                        type: 'string',
                        uniforms: {
                            required: true,
                            label: t`Étiquette`,
                            placeholder: t`Libellé`,
                        }
                    },
                    tooltip: {
                        type: 'string',
                        uniforms: {
                            label: t`Message d'aide`,
                            placeholder: t`Message d'aide`,
                            multiline: true,
                            rows: 2,
                        }
                    },
                    defaultNow: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Sélectionner par défaut la date et l'heure du jour`,
                        }
                    },
                    dateFilter: {
                        type: "string",
                        uniforms: {
                            showIf: (data) => data.picker !== formPlugin.DATE_PICKER[3],
                            label: t`Filtreur de date`,
                            allowedValues: formPlugin.DATE_FILTER_SELECT,
                        },
                    },
                    picker: {
                        type: "string",
                        uniforms: {
                            label: t`Mode`,
                            allowedValues: formPlugin.DATE_PICKER,
                        },
                    },
                }
            },
        },
    }
}
